import React, { useState } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { DashboardProject } from '../../../types/project';
import { PipelineSetup } from './components/PipelineSetup';
import { FabricationSetup } from './components/FabricationSetup';
import { Loader2, Plus, Cable, Wrench, Save } from 'lucide-react';
import { pipeSizes } from '../../../mocks/material';
import { Button } from '../../../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../components/ui/tabs';

interface ProjectSetupProps {
  project: DashboardProject;
}

export const ProjectSetup: React.FC<ProjectSetupProps> = ({ project }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [setupTab, setSetupTab] = useState<'pipeline' | 'fabrication'>(project.projectTypes.pipeline ? 'pipeline' : 'fabrication');

    // Add save setup function
    const handleSaveSetup = async () => {
      try {
        setIsLoading(true);
        // Here you would typically make an API call to save the setup data
        // For now, we'll simulate an API call with a timeout
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Show success message (you might want to add a toast notification here)
        alert('Setup saved successfully!');
      } catch (error) {
        // Show error message
        alert('Failed to save setup. Please try again.');
        console.error('Error saving setup:', error);
      } finally {
        setIsLoading(false);
      }
    };

  return (
    <Card className="border-none shadow-none">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold">Project Setup</h3>
          <Button
            onClick={handleSaveSetup}
            disabled={isLoading || pipeSizes.length === 0}
            size="sm"
            className="bg-[#e04530] text-white hover:bg-[#e04530]/90 flex items-center gap-2 min-w-[140px] justify-center"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save  Setup
              </>
            )}
          </Button>
        </div>

        {/* Setup Category Tabs */}
        <Tabs className="w-full" 
          value={setupTab}
          onValueChange={(value) => setSetupTab(value as 'pipeline' | 'fabrication')}
        >
          <div className="sticky top-0 bg-white rounded-t-lg z-10 border-b border-gray-200 rounded-t-lg">
            <TabsList  className="h-12 w-full justify-start gap-8 border-b border-gray-200 bg-transparent px-4 py-8"
            >
              {project.projectTypes.pipeline && (
                <TabsTrigger 
                className="data-[state=active]:border-[#e04530] data-[state=active]:text-[#e04530] relative px-1 pb-3 pt-2 text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px]"
                value="pipeline">
                  <Cable className="h-4 w-4 mr-2" />Pipeline
                </TabsTrigger>
              )}
              {project.projectTypes.fabrication && (
                <TabsTrigger
                className="data-[state=active]:border-[#e04530] data-[state=active]:text-[#e04530] relative px-1 pb-3 pt-2 text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px]" 
                value="fabrication">
                  <Wrench className="h-4 w-4 mr-2" />Fabrication
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          {project.projectTypes.pipeline && (
            <TabsContent value="pipeline">
              <PipelineSetup project={project} />
            </TabsContent>
          )}
          {project.projectTypes.fabrication && (
            <TabsContent value="fabrication">
              <FabricationSetup project={project} />
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
}; 